import React, { useState, useCallback } from "react";
import {
  useTranslate,
  Identifier,
  useDataProvider,
  FormTab,
} from "react-admin";
import { IDropDownAppData } from "../../../common/redux/reducers/appDataReducer";
import {
  Tooltip,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  MenuList,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import HourglassEmptyRoundedIcon from "@material-ui/icons/HourglassEmptyRounded";
import { InnerActionPermissionsTab } from "./action-permissions/InnerActionPermissionsTab";
import {
  RoleDto,
  RootActionPermissionGroup,
} from "../../../common/data/service-proxies";
import resourceNames from "../../../common/data/resourceNames";
import { useForm } from "react-final-form";

interface PermissionsTabProps {
  disabled?: boolean;
  roles?: IDropDownAppData<string>[];
  groupedPermissionDefinitions?: RootActionPermissionGroup[] | null;
}

export const ActionPermissionsTab = ({
  roles,
  groupedPermissionDefinitions,
  ...rest
}: PermissionsTabProps) => {
  const form = useForm();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [
    actionPermissionsCopyAnchor,
    setActionPermissionsCopyAnchor,
  ] = useState<HTMLButtonElement | null>(null);
  const [copying, setCopying] = useState<Identifier>();

  const isCopying = useCallback(
    (id?: Identifier | null) => {
      return copying === id;
    },
    [copying]
  );

  const handleActionPermissionsCopyClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActionPermissionsCopyAnchor(event.currentTarget);
  };

  const handleActionPermissionsCopyClose = () => {
    setActionPermissionsCopyAnchor(null);
  };

  const copyActionPermissionsFromRole = async (id: Identifier) => {
    setCopying(id);
    try {
      const { data } = await dataProvider.getOne(resourceNames.ADMIN_ROLES, {
        id,
      });
      const role = data as RoleDto;
      form.change("actionPermissions", role.actionPermissions);
    } finally {
      setCopying(undefined);
      handleActionPermissionsCopyClose();
    }
  };

  const actionPermissionsCopyOpen = Boolean(actionPermissionsCopyAnchor);
  return (
    <>
      <FormTab
        {...rest}
        label={
          <div className="tab-title-wrapper">
            {translate("resources.admin-roles.labels.actionPermissions")}
            {roles && roles.length > 0 && (
              <Tooltip
                title={`${translate(
                  "resources.admin-roles.labels.copyActionPermissions"
                )} ${translate("resources.admin-roles.labels.anotherRole")}`}
              >
                <IconButton onClick={handleActionPermissionsCopyClick}>
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        }
      >
        {groupedPermissionDefinitions && (
          <InnerActionPermissionsTab
            permissionDefinitions={groupedPermissionDefinitions}
          />
        )}
      </FormTab>
      <Popover
        open={actionPermissionsCopyOpen}
        anchorEl={actionPermissionsCopyAnchor}
        onClose={handleActionPermissionsCopyClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        getContentAnchorEl={null}
      >
        <MenuList>
          {roles?.map((x) => (
            <MenuItem
              key={x.id}
              onClick={() => copyActionPermissionsFromRole(x.id!)}
            >
              <ListItemIcon>
                {isCopying(x.id) ? (
                  <HourglassEmptyRoundedIcon />
                ) : (
                  <FileCopyIcon />
                )}
              </ListItemIcon>
              <ListItemText>
                {`${translate(
                  "resources.admin-roles.labels.copyActionPermissions"
                )} ${x.name}`}
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
